/**
 * AutomationStatusBadge Component
 *
 * Reusable status badge with color coding for automation rules and executions.
 * Used throughout the automation UI to display status in a consistent, visually
 * distinct manner with appropriate icons.
 */

import { memo, type ReactNode } from 'react';
import type { IAutomationStatusBadgeProps } from '@/@types/automation';
import { cn } from '@/lib/utils';
import {
  CheckCircle,
  PauseCircle,
  Clock,
  XCircle,
  AlertCircle,
  Loader2,
  SkipForward,
  FileEdit,
  Send,
} from 'lucide-react';

/**
 * Configuration for a single status type
 */
interface IStatusConfig {
  label: string;
  className: string;
  icon: ReactNode;
}

/**
 * Status configuration mapping
 * Maps each status to its label, styling, and icon
 */
const STATUS_CONFIG: Record<string, IStatusConfig> = {
  // Rule statuses
  active: {
    label: 'Active',
    className: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
    icon: <CheckCircle className="h-3 w-3" />,
  },
  paused: {
    label: 'Paused',
    className: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400',
    icon: <PauseCircle className="h-3 w-3" />,
  },
  draft: {
    label: 'Draft',
    className: 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-400',
    icon: <FileEdit className="h-3 w-3" />,
  },

  // Execution statuses
  pending: {
    label: 'Pending',
    className: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
    icon: <Clock className="h-3 w-3" />,
  },
  generating: {
    label: 'Generating',
    className: 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400',
    icon: <Loader2 className="h-3 w-3 animate-spin" />,
  },
  awaiting_approval: {
    label: 'Awaiting Approval',
    className: 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400',
    icon: <AlertCircle className="h-3 w-3" />,
  },
  approved: {
    label: 'Approved',
    className: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
    icon: <CheckCircle className="h-3 w-3" />,
  },
  rejected: {
    label: 'Rejected',
    className: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
    icon: <XCircle className="h-3 w-3" />,
  },
  published: {
    label: 'Published',
    className: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
    icon: <Send className="h-3 w-3" />,
  },
  skipped: {
    label: 'Skipped',
    className: 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400',
    icon: <SkipForward className="h-3 w-3" />,
  },
  failed: {
    label: 'Failed',
    className: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
    icon: <XCircle className="h-3 w-3" />,
  },
};

/**
 * Base class names applied to all status badges
 */
const BASE_CLASSES = 'inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium';

/**
 * Fallback styling for unknown statuses
 */
const FALLBACK_CLASSES = 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-400';

export const AutomationStatusBadge = memo(function AutomationStatusBadge({
  status,
  className,
}: IAutomationStatusBadgeProps) {
  const config = STATUS_CONFIG[status];

  // Fallback for unknown status
  if (!config) {
    return <span className={cn(BASE_CLASSES, FALLBACK_CLASSES, className)}>{status}</span>;
  }

  return (
    <span className={cn(BASE_CLASSES, config.className, className)}>
      {config.icon}
      {config.label}
    </span>
  );
});
